/**
 * Error plates — the one look every error in the game wears.
 *
 * One slice of the base-view stylesheet. `styles/index.ts` concatenates
 * every slice into the single sheet `injectStyles` mounts — the split is
 * about OWNERSHIP, not about emitting more than one style element.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below
 * is one template literal and a stray backtick terminates it. That has
 * broken the build twice, both times with esbuild pointing at a line ten
 * rows away from the cause. Write class names in plain text.
 *
 * ─── Why a slice of its own, loaded late ────────────────────────────────────
 * Errors are drawn by the shared window chrome (bv-win-err), the Survivor
 * Record (bv-pd-msg), the house (bv-home-error) and the duel HUD
 * (bv-duel-error), and each of those slices already styles its own. This one
 * comes after all of them in styles/index.ts so that ONE recipe wins for every
 * plate at equal specificity, instead of an error being rust in one window and
 * amber in the next.
 *
 * The recipe: a hazard-striped edge, a warning badge, riveted rust-red steel,
 * streaks and scratches, torn corners. The motion is a class, bv-err-in, added
 * by audio/error-fx.ts when a NEW error appears; a plate that was only
 * repainted never gets it, which is what stops a standing error from shaking
 * once a second.
 */
export default `
@keyframes bverr-in {
  0%   { opacity: 0; transform: translateX(-18px) skewX(-7deg); filter: brightness(2.4) saturate(1.6); }
  16%  { opacity: 1; transform: translateX(9px); }
  30%  { transform: translateX(-7px); }
  44%  { transform: translateX(5px); filter: brightness(1.5); }
  58%  { transform: translateX(-3px); }
  75%  { transform: translateX(1px); }
  100% { opacity: 1; transform: none; filter: none; }
}
/* The duel error is centred with a transform of its own, so it cannot shake
   with one: it drops in and flashes instead. */
@keyframes bverr-pop {
  0%   { opacity: 0; filter: brightness(2.4) saturate(1.6); }
  30%  { opacity: 1; filter: brightness(1.6); }
  100% { opacity: 1; filter: none; }
}
/* The red flash that sweeps off the plate as it lands. */
@keyframes bverr-flash {
  0%   { opacity: 0.75; }
  100% { opacity: 0; }
}
/* The badge breathes while the error stands, slowly, so it reads as an alarm
   that is still on and not a sticker. */
@keyframes bverr-badge {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,90,60,0), inset 0 1px 2px rgba(0,0,0,0.6); }
  50%      { box-shadow: 0 0 9px 2px rgba(255,90,60,0.65), inset 0 1px 2px rgba(0,0,0,0.6); }
}

.bv-win-err, .bv-pd-msg, .bv-home-error, .bv-duel-error {
  position: relative; overflow: hidden;
  padding: 9px 14px 9px 42px;
  border: 2px solid #1a0a07; border-radius: 3px;
  color: #ffdccf; font: 0.85rem/1.4 var(--font-technical); letter-spacing: 0.02em;
  text-shadow: 0 1px 0 #000, 0 0 8px rgba(0,0,0,0.5);
  background:
    /* hazard stripe down the left edge */
    repeating-linear-gradient(135deg, #17100a 0 6px, #c9902c 6px 12px) left top / 12px 100% no-repeat,
    /* rust running down from the top edge, two streaks, not symmetrical */
    linear-gradient(180deg, rgba(190,90,36,0.4), transparent) 34% 0 / 12px 100% no-repeat,
    linear-gradient(180deg, rgba(160,70,26,0.32), transparent) 78% 0 / 8px 100% no-repeat,
    /* scratches and speckle */
    linear-gradient(112deg, transparent 38%, rgba(255,225,200,0.13) 38.4%, transparent 39.2%),
    linear-gradient(74deg, transparent 63%, rgba(0,0,0,0.4) 63.3%, transparent 64.1%),
    radial-gradient(circle at 14% 30%, rgba(255,235,220,0.1) 0 1px, transparent 1.6px) 0 0 / 11px 8px,
    radial-gradient(circle at 66% 72%, rgba(0,0,0,0.45) 0 1.2px, transparent 1.8px) 0 0 / 14px 10px,
    linear-gradient(180deg, #632a1b 0%, #43180f 55%, #260d08 100%);
  box-shadow: inset 0 1px 0 rgba(255,190,160,0.28), inset 0 -2px 0 rgba(0,0,0,0.55),
              inset 0 0 20px rgba(0,0,0,0.5), 0 3px 0 #0a0302, 0 6px 12px rgba(0,0,0,0.6);
  /* torn corners: top-right and bottom-left clipped, the way a label is torn */
  clip-path: polygon(0 0, calc(100% - 7px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 5px));
}
/* The Record's message sits at the panel's margin, as it always has. */
.bv-pd-msg { margin: 8px 18px 0; }
.bv-home-error { margin-top: 8px; }
.bv-duel-error {
  /* Absolute, as in duel.css.ts: it is centred in the HUD, and the shared
     recipe above says relative. */
  position: absolute;
  padding: 16px 20px; text-align: center;
  background:
    linear-gradient(180deg, rgba(190,90,36,0.3), transparent 55%) 30% 0 / 14px 100% no-repeat,
    radial-gradient(circle at 14% 30%, rgba(255,235,220,0.08) 0 1px, transparent 1.6px) 0 0 / 11px 8px,
    linear-gradient(180deg, #4a1d12, #1d0a07);
}

/* The warning badge: a riveted disc with an exclamation mark. Not on the duel
   plate, whose text is centred. */
.bv-win-err::before, .bv-pd-msg::before, .bv-home-error::before {
  content: "!"; position: absolute; left: 20px; top: 50%; margin-top: -9px;
  width: 18px; height: 18px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font: 700 12px/1 var(--font-display); color: #1a0a07; text-shadow: 0 1px 0 rgba(255,235,200,0.5);
  background: radial-gradient(circle at 35% 30%, #ffe3a8, #e0a53c 55%, #8a5a18);
  border: 1px solid #1a0a07;
  animation: bverr-badge 2.2s ease-in-out infinite;
}

/* Entry. The class is added by error-fx.ts and removed when it finishes. */
.bv-win-err.bv-err-in, .bv-pd-msg.bv-err-in, .bv-home-error.bv-err-in { animation: bverr-in 0.6s cubic-bezier(0.2, 0.8, 0.3, 1) both; }
.bv-duel-error.bv-err-in { animation: bverr-pop 0.5s ease-out both; }
.bv-win-err.bv-err-in::after, .bv-pd-msg.bv-err-in::after,
.bv-home-error.bv-err-in::after, .bv-duel-error.bv-err-in::after {
  content: ""; position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(120% 160% at 10% 50%, rgba(255,90,60,0.7), transparent 70%);
  animation: bverr-flash 0.8s ease-out both;
}

@media (prefers-reduced-motion: reduce) {
  .bv-win-err.bv-err-in, .bv-pd-msg.bv-err-in, .bv-home-error.bv-err-in, .bv-duel-error.bv-err-in { animation: none; }
  .bv-err-in::after { animation: none; opacity: 0; }
  .bv-win-err::before, .bv-pd-msg::before, .bv-home-error::before { animation: none; }
}
`;
